import { useToast } from '@chakra-ui/react';
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import type {
  CandidateBasicDtoPagedResponseDto,
  CandidateListType,
  CandidateSortColumn,
  CustomSortOrder,
  InformationalTextRequest,
  InstructionVideoRequest,
  InterviewDetailResponse,
  InterviewQuestionsResponse,
  InterviewStatus,
  InviteCandidateRequest,
  PublicInterviewInfo,
  PutCandidateRequest,
  PutQuestionNoteRequest,
  ReminderGroupDtoPagedResponseDto,
  UpdateInterviewQuestionRequest,
  UpdateInterviewRequest,
  UpdateInterviewSettings,
  UpdateJobDescriptionRequest,
} from '@/services/api';
import { InterviewService } from '@/services/api';

import { adminCandidateKeys } from '../admin-candidate/useAdminCandidateQueries';

export const adminInterviewKeys = {
  getInterview: (params: GetInterviewQueryParams) =>
    ['getAllInterviews', params] as const,

  getAllInterviewsCandidatesBase: () => ['getAllInterviewsCandidates'] as const,
  getAllInterviewsCandidates: (params: GetInterviewCandidatesParams) =>
    [...adminInterviewKeys.getAllInterviewsCandidatesBase(), params] as const,
  getAllInterviewQuestions: (params: GetAllInterviewQuestionsQueryParams) =>
    ['getAllInterviewQuestions', params] as const,
  getInterviewReminders: (params: GetInterviewRemindersQueryParams) =>
    ['getInterviewReminders', params] as const,
};

type GetInterviewQueryParams = {
  interviewsId: string;
};

export const useGetInterviewQuery = (
  params: GetInterviewQueryParams,
  options?: Partial<UseQueryOptions<InterviewDetailResponse, AxiosError>>
) => {
  return useQuery<InterviewDetailResponse, AxiosError>({
    queryKey: adminInterviewKeys.getInterview(params),
    queryFn: () =>
      InterviewService.getAllInterviews(params, options?.axiosRequestConfig),
    ...options,
  });
};

type GetAllInterviewQuestionsQueryParams = {
  interviewId: string;
};

type GetInterviewRemindersQueryParams = {
  remindDateFrom?: string;
  remindDateTo?: string;
  interviewId?: string;
  candidateId?: string;
  isManual?: boolean;
  page?: number;
  size?: number;
};

export const useGetAllInterviewQuestionsQuery = (
  params: GetAllInterviewQuestionsQueryParams,
  options?: Partial<UseQueryOptions<InterviewQuestionsResponse, AxiosError>>
) => {
  return useQuery<InterviewQuestionsResponse, AxiosError>({
    queryKey: adminInterviewKeys.getAllInterviewQuestions(params),
    queryFn: () =>
      InterviewService.getAllInterviewQuestions(
        { id: params.interviewId },
        options?.axiosRequestConfig
      ),
    ...options,
  });
};

export const useGetInterviewRemindersQuery = (
  params: GetInterviewRemindersQueryParams,
  options?: Partial<
    UseQueryOptions<ReminderGroupDtoPagedResponseDto, AxiosError>
  >
) => {
  return useQuery<ReminderGroupDtoPagedResponseDto, AxiosError>({
    queryKey: adminInterviewKeys.getInterviewReminders(params),
    queryFn: () =>
      InterviewService.getInterviewReminders(
        params,
        options?.axiosRequestConfig
      ),
    ...options,
  });
};

type GetInterviewCandidatesParams = {
  interviewsId: string;
  size: number;
  page: number;
  q?: string;
  aiScoreFrom?: number;
  aiScoreTo?: number;
  dateFrom?: Date;
  dateTo?: Date;
  status?: Array<InterviewStatus>;
  ratings?: Array<number>;
  sortColumn?: CandidateSortColumn;
  sortOrder?: CustomSortOrder;
  candidateListType?: CandidateListType;
};
export const useGetInterviewCandidatesQuery = (
  params: GetInterviewCandidatesParams,
  options?: Partial<
    UseQueryOptions<CandidateBasicDtoPagedResponseDto, AxiosError>
  >
) => {
  return useQuery<CandidateBasicDtoPagedResponseDto, AxiosError>({
    queryKey: adminInterviewKeys.getAllInterviewsCandidates(params),
    queryFn: () =>
      InterviewService.getAllInterviewsCandidates(
        {
          ...params,
          dateFrom: params.dateFrom?.toISOString(),
          dateTo: params.dateTo?.toISOString(),
        },
        options?.axiosRequestConfig
      ),
    refetchInterval(query) {
      return query.state.data?.data?.some((x) => x.reAnalyzing) ? 1000 : false;
    },
    ...options,
  });
};

export const useCandidatesAddMutation = (
  interviewsId: string,
  selectedTemplateId: string | undefined,
  options?: UseMutationOptions<void, AxiosError, InviteCandidateRequest>
) => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, InviteCandidateRequest>({
    mutationFn: async (body) => {
      return InterviewService.putAllInterviewsCandidatesAdd({
        interviewsId,
        body: {
          ...body,
          mailTemplateId: selectedTemplateId,
        },
      });
    },
    ...options,
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries({
        queryKey: adminInterviewKeys.getAllInterviewsCandidatesBase(),
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useEditInterviewQuestionsMutation = (
  options?: UseMutationOptions<
    UpdateInterviewQuestionRequest,
    AxiosError,
    { interviewId: string; body: UpdateInterviewQuestionRequest }
  >
) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation(['interview']);

  return useMutation<
    UpdateInterviewQuestionRequest,
    AxiosError,
    { interviewId: string; body: UpdateInterviewQuestionRequest }
  >({
    mutationFn: async ({ interviewId, body }) => {
      return InterviewService.putAllInterviewQuestions({
        id: interviewId,
        body,
      });
    },
    ...options,
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries({
        queryKey: adminInterviewKeys.getAllInterviewQuestions({
          interviewId: variables.interviewId,
        }),
      });

      queryClient.invalidateQueries({
        queryKey: adminInterviewKeys.getInterview({
          interviewsId: variables.interviewId,
        }),
      });

      toast({
        title: t('interview:editQuestionsSuccess'),
        status: 'success',
      });

      options?.onSuccess?.(data, variables, context);
    },
    onError() {
      toast({
        title: t('interview:editQuestionsFailed'),
        status: 'error',
      });
    },
  });
};

export const useUpdateInterviewMutation = (
  options?: Partial<
    UseMutationOptions<
      void,
      AxiosError,
      { interviewsId: string; body: UpdateInterviewRequest }
    >
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    AxiosError,
    { interviewsId: string; body: UpdateInterviewRequest }
  >({
    mutationFn: (params) => InterviewService.putAllInterviews(params),
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: adminInterviewKeys.getInterview({
          interviewsId: variables.interviewsId,
        }),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

type AddJobDescriptionMutation = {
  interviewId: string;
  body?: UpdateJobDescriptionRequest;
};

export const useAddJobDescriptionMutation = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, AddJobDescriptionMutation>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, AddJobDescriptionMutation>({
    mutationFn: (params) =>
      InterviewService.putAllInterviewJobDescription(params),
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: adminInterviewKeys.getInterview({
          interviewsId: variables.interviewId,
        }),
      });
      await queryClient.invalidateQueries({
        queryKey: adminInterviewKeys.getAllInterviewsCandidatesBase(),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

type AddInformationalTextMutation = {
  interviewId: string;
  body?: InformationalTextRequest;
};

export const useAddInformationalTextMutation = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, AddInformationalTextMutation>
  >
) => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, AddInformationalTextMutation>({
    mutationFn: (params) =>
      InterviewService.putAllInterviewInformationalText({
        id: params.interviewId,
        body: params.body,
      }),
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: adminInterviewKeys.getInterview({
          interviewsId: variables.interviewId,
        }),
      });
      await queryClient.invalidateQueries({
        queryKey: adminInterviewKeys.getAllInterviewsCandidatesBase(),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

type AddInterviewInstructionVideoMutation = {
  id: string;
  body?: InstructionVideoRequest;
};

export const useAddInterviewInstructionVideoMutation = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, AddInterviewInstructionVideoMutation>
  >
) => {
  return useMutation<void, AxiosError, AddInterviewInstructionVideoMutation>({
    mutationFn: (params) =>
      InterviewService.putAllInterviewInstructionVideo(params),
    ...options,
  });
};

type DeleteInterviewInstructionVideoMutation = {
  id: string;
};

export const useDeleteInterviewInstructionVideoMutation = (
  options?: Partial<
    UseMutationOptions<
      void,
      AxiosError,
      DeleteInterviewInstructionVideoMutation
    >
  >
) => {
  return useMutation<void, AxiosError, DeleteInterviewInstructionVideoMutation>(
    {
      mutationFn: (params) =>
        InterviewService.deleteInterviewInstructionVideoById(params),
      ...options,
    }
  );
};

export const useEditCandidateRequestMutation = (
  options?: UseMutationOptions<
    void,
    AxiosError,
    { interviewsId: string; candidateId: string; body: PutCandidateRequest }
  >
) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation('candidate');

  return useMutation<
    void,
    AxiosError,
    { interviewsId: string; candidateId: string; body: PutCandidateRequest }
  >({
    mutationFn: async ({ body, interviewsId, candidateId }) => {
      return InterviewService.putInterviewsCandidatesById(
        {
          interviewsId,
          candidateId,
          body,
        },
        options?.axiosRequestConfig
      );
    },
    ...options,
    async onSuccess(data, variables, context) {
      await queryClient.invalidateQueries({
        queryKey: adminInterviewKeys.getInterview({
          interviewsId: variables.interviewsId,
        }),
      });

      await queryClient.invalidateQueries({
        queryKey: adminInterviewKeys.getAllInterviewsCandidatesBase(),
      });

      toast({
        title: t('inviteNewCandidate.editCandidateSuccess'),
        status: 'success',
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useAddTemplateToInterviewMutation = (
  options?: Partial<UseMutationOptions<void, AxiosError, string>>
) =>
  useMutation<void, AxiosError, string>({
    mutationFn: (interviewId) =>
      InterviewService.putAllInterviewsInterviewTemplates({
        interviewsId: interviewId,
      }),
    ...options,
  });

type GetInterviewCompanyInfoQueryParams = {
  interviewId: string;
  companyId: string;
};

export const useGetInterviewCompanyInfoQuery = (
  params: GetInterviewCompanyInfoQueryParams,
  options?: Partial<UseQueryOptions<PublicInterviewInfo, AxiosError>>
) => {
  return useQuery<PublicInterviewInfo, AxiosError>({
    queryKey: adminInterviewKeys.getInterview({
      interviewsId: params.interviewId,
    }),
    queryFn: () =>
      InterviewService.getInterviewsCompanyInfoById(
        { interviewId: params.interviewId, companyId: params.companyId },
        options?.axiosRequestConfig
      ),
    ...options,
  });
};

type UpdateInterviewSettingsMutation = {
  interviewsId: string;
  body?: UpdateInterviewSettings;
};

export const useUpdateInterviewSettingsMutation = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, UpdateInterviewSettingsMutation>
  >
) => {
  return useMutation<void, AxiosError, UpdateInterviewSettingsMutation>({
    mutationFn: (params) => InterviewService.putAllInterviewsSettings(params),
    ...options,
  });
};

export const useSaveNoteMutation = (
  params: {
    interviewsId: string;
    candidateId: string;
    questionId: string;
  },
  options?: UseMutationOptions<void, AxiosError, PutQuestionNoteRequest>
) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation(['interview']);

  return useMutation<void, AxiosError, PutQuestionNoteRequest>({
    mutationFn: async (body) => {
      return InterviewService.putInterviewsCandidatesQuestionsNoteById({
        interviewsId: params.interviewsId,
        candidateId: params.candidateId,
        questionId: params.questionId,
        body: {
          note: body.note,
        },
      });
    },
    ...options,
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries({
        queryKey: adminCandidateKeys.base(),
      });

      toast({
        title: t('interview:videoInterview.noteSaved'),
        status: 'success',
      });
      options?.onSuccess?.(data, variables, context);
    },
    onError() {
      toast({
        title: t('interview:videoInterview.noteSaveError'),
        status: 'error',
      });
    },
  });
};
